import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { SplitRowQuarter } from "../../../../assets/components/ButtonGroup";
import { Chip } from "../../../../assets/components/Chip";
import PlaceholderB from "../../../../assets/components/PlaceholderB";
import { BottomWithButton } from "../../../../assets/components/BottomSheetGroup";

const THEME_OPTIONS = ["매장 홍보", "상품 소개", "이벤트"];

// ── helpers ─────────────────────────────────────────────
const norm = (s = "") => String(s).trim().replace(/\s+/g, "").toLowerCase();
const sanitize = (x) => {
  const s = String(x ?? "").trim();
  return s === "" || s === "null" || s === "undefined" ? "" : s;
};
// "1|2|3" 또는 라벨 → 정확 라벨
function mapAiToTheme(val) {
  if (val == null) return null;
  const s = String(val).trim();
  const byCode = { "1": "매장 홍보", "2": "상품 소개", "3": "이벤트" };
  if (byCode[s]) return byCode[s];
  const hit = THEME_OPTIONS.find((t) => norm(t) === norm(s));
  return hit || null;
}

// 안내문
function GuidanceByTheme({ theme, rightNode }) {
  const COPY = {
    이벤트: {
      title: "이벤트 내용",
      required: false,
      hint: "진행 중인 이벤트를 작성해 주세요.",
    },
    default: {
      title: "광고 내용",
      required: false,
      hint: "위치/편의/특징을 작성해주세요.",
    },
  };
  const item = COPY[theme] ?? COPY.default;
  return (
    <div className="flex flex-col gap-[8px]">
      <div className="flex items-center justify-between gap-[8px]">
        <p className="text-t4 text-text-default flex-1">
          {item.title}
          <span className={`${item.required ? "text-text-danger" : "text-text-disabled"}`}>
            {item.required ? "(필수)" : "(선택)"}
          </span>
        </p>
        {rightNode && <div className="shrink-0">{rightNode}</div>}
      </div>
      <p className="text-b1 text-text-subtler">{item.hint}</p>
    </div>
  );
}

/**
 * props
 * - isOpen, onClose
 * - value, detailValue
 * - aiTitle, aiReco
 * - data, exData, updateData
 * - onConfirm({ theme, customText })
 */
export default function EventTitleSheet({
  isOpen,
  onClose,
  value,
  detailValue,
  aiTitle,
  aiReco,
  data,
  exData,
  updateData,
  onConfirm,
  height = "h-[70%]",
}) {
  // 🔹 AgeSelectSheet처럼: AI 추천값 계산 (props 기반)
  const recommendedTheme = useMemo(
    () => mapAiToTheme(sanitize(aiTitle)) || mapAiToTheme(sanitize(aiReco)),
    [aiTitle, aiReco]
  );

  // 시트 내부에서만 쓰는 선택값 / 텍스트
  const [selectedTheme, setSelectedTheme] = useState("");
  const [customText, setCustomText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  // 🔹 시트가 열릴 때: value 우선 → 없으면 AI 추천 → 없으면 ""
  //   (AgeSelectSheet와 동일한 우선순위)
  useEffect(() => {
    if (!isOpen) return;

    const raw = sanitize(value);
    const explicit = mapAiToTheme(raw) || raw; // 숫자/라벨 모두 허용
    const exists =
      !!explicit && THEME_OPTIONS.some((t) => norm(t) === norm(explicit));

    const initial = exists
      ? THEME_OPTIONS.find((t) => norm(t) === norm(explicit)) || ""
      : recommendedTheme || "";

    setSelectedTheme(initial);
    setCustomText(detailValue ?? "");
    setIsLoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);


  // const isValid =
  //   !!selectedTheme &&
  //   (selectedTheme !== "상품소개" || (customText && customText.trim() !== ""));

  const handleSave = () => {
    // if (!isValid) return;
    onConfirm?.({ theme: selectedTheme, customText });
    onClose?.();
  };

  // AI 자동 생성 (data.theme 기반 + updateData로 반영)
  const genMarketingCopy = async () => {
    const themeToUse = selectedTheme || data?.theme;
    if (!themeToUse) {
      alert("주제를 선택해 주세요.");
      return;
    }
    // 부모에 theme이 없다면 먼저 동기화 (복사 생성 편의를 위해 유지)
    if (!data?.theme) {
      updateData?.({ theme: themeToUse });
    }

    setIsLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_FASTAPI_ADS_URL}/ads/event/gen/copy`,
        {
          store_name: localStorage.getItem("storeName") || "",
          main: exData?.main,
          road_name: localStorage.getItem("roadName") || "",
          category: data?.category || data?.customMenu,
          temp: exData?.temp,
          theme: themeToUse,
        },
        { headers: { "Content-Type": "application/json" } }
      );
      const text = response?.data?.copyright ?? "";
      setCustomText(text);
    } catch (error) {
      console.error("자동화 광고 생성 중 오류:", error);
      alert("광고 생성에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <BottomWithButton isOpen={isOpen} onClose={onClose}
      title="이벤트 내용"
      subtitle="어떤 이벤트를 진행하나요?"
      // height={height}
      onSubmit={handleSave}
      buttonLabel="저장"
      height={height}
      // buttonDisabled={!isValid}
      // 하단 버튼을 SplitRowQuarter로 커스터마이즈
      renderButton={({ onClick, disabled }) => (
        <SplitRowQuarter
          leftText="닫기"
          onLeft={onClose}
          rightType="fill"
          rightText="저장"
          onRight={onClick}
          rightDisabled={disabled}
        />
      )}
    >
      {/* 안내 + 입력 + 작은 'AI 자동 생성' 버튼 */}
      <div className="flex flex-col gap-[16px]">
        <div className="">
          <GuidanceByTheme
            theme={selectedTheme || "default"}
            rightNode={                
              <Chip
                chip="f"
                label="AI 자동 생성"
                onClick={isLoading ? undefined : genMarketingCopy}
              />
            }
          />
        </div>

        <PlaceholderB
          maxLength={100}
          rows={5}
          showCounter
          size="lg"
          placeholder="예) 11시 전 주문 시 아메리카노 2,000원 행사 진행중!"
          value={customText}
          onChange={(e) => {
            setCustomText(e.target.value);
          }}
        />
      </div>
    </BottomWithButton>
  );
}
